import { type App, type Component, type ComponentPublicInstance, type VNode } from 'vue'
import type { DialogConfig } from '@/types/dialog'
import Dialog from '@/components/base/dialog/Dialog.vue'
import { createVNode, render } from 'vue'
// 定义 Dialog 组件实例类型
type DialogInstance = ComponentPublicInstance & {
  show: (config: DialogConfig) => void
}

// Dialog 实例管理
class DialogService {
  private instances: Array<{ vnode: VNode; container: HTMLElement }> = []
  private app: App | null = null

  setApp(app: App) {
    this.app = app
    return this
  }
  /**
   * 显示确认对话框
   */
  confirm(config: Omit<DialogConfig, 'showCancel'>) {
    return new Promise<boolean>((resolve) => {
      this.show({
        ...config,
        showCancel: true,
        onConfirm: () => {
          resolve(true)
        },
        onCancel: () => {
          resolve(false)
        },
        onClose: () => {
          resolve(false)
        }
      })
    })
  }

  /**
   * 显示警告对话框
   */
  alert(config: Omit<DialogConfig, 'showCancel'>) {
    return new Promise<void>((resolve) => {
      this.show({
        ...config,
        showCancel: false,
        onConfirm: () => {
          resolve()
        },
        onClose: () => {
          resolve()
        }
      })
    })
  }

  /**
   * 显示自定义对话框
   */
  show(config: DialogConfig) {
    const container = document.createElement('div')
    document.body.appendChild(container)

    const vnode = createVNode(Dialog)

    // 继承主应用上下文（i18n等）
    if (this.app) vnode.appContext = this.app._context

    render(vnode, container)

    // 记录实例
    this.instances.push({ vnode, container })

    const instance = vnode.component?.exposed as DialogInstance

    // 显示对话框
    instance.show({
      ...config,
      onClose: () => {
        config.onClose?.()
        this.destroyInstance(vnode, container)
      }
    })

    return instance
  }

  /**
   * 显示自定义组件对话框
   */
  component(component: Component, props?: Record<string, unknown>, config?: Partial<DialogConfig>) {
    return this.show({
      content: component,
      props,
      ...config
    })
  }

  /**
   * 关闭所有对话框
   */
  closeAll() {
    this.instances.forEach(({ vnode, container }) => this.destroyInstance(vnode, container))
    this.instances = []
  }

  /**
   * 销毁实例
   */
  private destroyInstance(vnode: VNode, container: HTMLElement) {
    setTimeout(() => {
      render(null, container)
      container.parentNode?.removeChild(container)
      // 从实例列表中移除
      const idx = this.instances.findIndex((i) => i.vnode === vnode && i.container === container)
      if (idx > -1) this.instances.splice(idx, 1)
    }, 300) // 等待动画完成
  }
}

// 创建单例实例
const dialog = new DialogService()

export default dialog

// 类型导出
export type { DialogAction, DialogConfig } from '@/types/dialog'
